import React, { memo, useEffect, useContext } from 'react';
import { get } from 'lodash';
import { useSelector } from 'react-redux';
import { CssBaseline } from '@material-ui/core';

import { Paper } from '../material-ui/Paper';
import { AdditionalInfoSideBar } from '../AdditionalInfoSideBar/AdditionalInfoSideBar';
import { ApplicationBar } from '../AppBar/AppBar';
import { AttachmentSidebar } from '../AttachmentSidebar/AttachmentSidebar';
import { BottomFlightBar } from '../BottomFlightBar/BottomFlightBar';
import { FilterSidebar } from '../FilterSidebar/FilterSidebar';
import { FlightWizardStepper } from '../FlightWizardPage/FlightWizardStepper/FlightWizardStepper';
import { MessageDialog } from '../MessageDialog/MessageDialog';
import { MessengerSyncManager } from '../MessengerSyncManager/MessengerSyncManager';
import { NotificationSidebar } from '../NotificationSidebar/NotificationSidebar';
import { ProgressIndicatorOverlay } from '../Progress/ProgressIndicatorOverlay/ProgressIndicatorOverlay';
import { ProgressIndicatorLinear } from '../Progress/ProgressIndicatorLinear/ProgressIndicatorLinear';
import { Sidebar } from '../Sidebar/Sidebar';
import { getCurrentLegCrewType } from '../../selectors/screen';
import { CrewTypes } from '../../service/constants';
import { setHtmlOverflow } from '../hooks/useScrollFix';
import { useBackButton } from '../hooks/useBackButton';
import { useWhatsNewModal } from '../hooks/useWhatsNewModal';
import { ShortcutsPanel } from '../ShortcutsPanel/ShortcutsPanel';
import { SidebarContext } from './ContextProvider';

import './PageLayout.scss';

const routesWithoutFlightBar = ['flight-task', 'reports', 'library', 'messages'];
const STEPPER_HEIGHT = 56;
const BOTTOMNAVBAR_HEIGHT = 56;

const getContentStyle = (showStepper, showBottomNavigationBar) => {
    let padding = 64;

    if (showStepper) {
        padding += STEPPER_HEIGHT;
    }

    const paddingBottom = showBottomNavigationBar ? BOTTOMNAVBAR_HEIGHT : 0;

    return {
        paddingTop: `${padding}px`,
        paddingBottom: `${paddingBottom}px`,
    };
};

export const PageLayout = memo((props) => {
    const crewType = useSelector(getCurrentLegCrewType);
    const pathname = useSelector((state) => get(state, 'router.location.pathname'));
    const page = pathname.substring(1);
    const needHideStepper = page !== 'flight-info' || crewType !== CrewTypes.PCT_CABINSTAFF;
    const showStepper = !needHideStepper;
    const showBottomFlightBar = !(routesWithoutFlightBar.indexOf(page) !== -1);
    const {
        sidebarVisibility: {
            notificationSidebarVisibility,
            attachmentSidebarVisibility,
            filterSidebarVisibility,
            additionalInfoSidebar: { visibility: AdditionalSidebarVisibilit = false } = {},
        },
    } = useContext(SidebarContext);

    const disableOverflow =
        notificationSidebarVisibility || attachmentSidebarVisibility || filterSidebarVisibility || AdditionalSidebarVisibilit || false;
    useEffect(() => {
        setHtmlOverflow(disableOverflow);
    }, [disableOverflow]);

    useBackButton(disableOverflow);
    useWhatsNewModal();

    return (
        <>
            <CssBaseline />
            <ProgressIndicatorOverlay />
            <ApplicationBar />
            <Sidebar navigationBarVisibility={showBottomFlightBar} />
            <NotificationSidebar navigationBarVisibility={showBottomFlightBar} />
            <AttachmentSidebar />
            <AdditionalInfoSideBar navigationBarVisibility={showBottomFlightBar} />
            <ShortcutsPanel />
            {!needHideStepper && <FlightWizardStepper />}
            <Paper
                className={`page-layout__content${page === 'messages' ? ' page-layout__content--messenger' : ''}`}
                style={getContentStyle(showStepper, showBottomFlightBar)}
            >
                <ProgressIndicatorLinear />
                {props.children}
            </Paper>
            <MessageDialog />
            <MessengerSyncManager />
            <FilterSidebar navigationBarVisibility={showBottomFlightBar} />
            {showBottomFlightBar && <BottomFlightBar />}
        </>
    );
});

PageLayout.displayName = 'PageLayout';
